// Session snapshots per-device para el swap de RoleToggle en modo demo.
// Las sesiones capturadas viven SOLO en localStorage del device — nunca
// en el bundle. El bundle público no expone ni passwords ni tokens.
//
// Setup per-device (documentado en docs/decisions/demo-mode.md):
//   1. Ale visita gonix.jeroz.dev/?demo=1 → flag ON.
//   2. Login como kine (jerozalejandro) → AuthProvider auto-captura la
//      sesión bajo gonix_demo_session_kine.
//   3. Logout.
//   4. Login como paciente (ajeroz) → AuthProvider auto-captura bajo
//      gonix_demo_session_paciente.
//   5. Con ambas capturadas, el RoleToggle Kine↔Paciente hace swap real
//      via supabase.auth.setSession(snapshot).
//
// Los tokens tienen expiración natural de Supabase (~1h access, refresh
// sin límite hasta revocation). Si el refresh_token expiró, setSession()
// falla y el user cae en anonymous → Ale re-loguea manualmente para
// re-capturar.

import type { Session } from "@supabase/supabase-js";

const KINE_KEY = "gonix_demo_session_kine";
const PACIENTE_KEY = "gonix_demo_session_paciente";

export type DemoRole = "kine" | "paciente";

function keyFor(role: DemoRole): string {
  return role === "kine" ? KINE_KEY : PACIENTE_KEY;
}

/** Persiste el session completo (incluye access_token, refresh_token,
 *  user, expires_at) bajo la clave del rol. Idempotente — sobreescribe
 *  cualquier snapshot previo del mismo rol. */
export function captureSession(role: DemoRole, session: Session): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(keyFor(role), JSON.stringify(session));
  } catch {
    // Storage bloqueado — no-op silencioso.
  }
}

/** Devuelve el snapshot capturado del rol, o null si no hay o el JSON
 *  quedó corrupto. */
export function getStoredSession(role: DemoRole): Session | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(keyFor(role));
    if (!raw) return null;
    return JSON.parse(raw) as Session;
  } catch {
    return null;
  }
}

/** Predicado de conveniencia para UI (habilitar/deshabilitar botón del
 *  toggle según si el snapshot del otro rol está disponible). */
export function hasStoredSession(role: DemoRole): boolean {
  return getStoredSession(role) !== null;
}

/** Borra ambos snapshots. Llamado desde setDemoMode(false) para no dejar
 *  tokens huérfanos si el device pasa a otro uso. */
export function clearStoredSessions(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(KINE_KEY);
    window.localStorage.removeItem(PACIENTE_KEY);
  } catch {
    // Storage bloqueado — no-op.
  }
}
